import { format, addMonths, setDate, isBefore, startOfDay } from 'date-fns';
import { parseDateOnly } from '@/lib/utils';

type ProgramLike = {
  name?: string | null;
  price?: number | null;
  deposit_amount?: number | null;
  full_session_price?: number | null;
  payment_plan_installments?: number | null;
  payment_plan_frequency?: string | null;
  recurring_first_payment_date?: string | null;
  final_payment_due_date?: string | null;
  payment_due_day_of_month?: number | null;
  payment_charge_date?: string | null;
  payment_due_type?: string | null;
  monthly_subscription?: boolean | null;
  pay_as_you_go?: boolean | null;
  billing_frequency?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  program_type?: string | null;
};

export type PaymentOptionLike = 'full' | 'deposit' | 'payment_plan' | 'recurring' | 'payg' | null | undefined;

function ordinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * First real charge date for a deferred program (recurring/PAYG).
 */
export function getFirstBillDate(program: ProgramLike): Date | null {
  if (program.recurring_first_payment_date) {
    try {
      return parseDateOnly(program.recurring_first_payment_date);
    } catch {}
  }

  const day = program.payment_due_day_of_month;
  if (day && day >= 1 && day <= 31) {
    const today = startOfDay(new Date());
    let candidate = setDate(today, day);
    if (isBefore(candidate, today)) candidate = setDate(addMonths(today, 1), day);
    return candidate;
  }

  if (program.payment_charge_date) {
    try {
      return parseDateOnly(program.payment_charge_date);
    } catch {}
  }
  if (program.start_date) {
    try {
      return parseDateOnly(program.start_date);
    } catch {}
  }
  return null;
}

export function formatFirstBillDate(program: ProgramLike): string | null {
  const d = getFirstBillDate(program);
  return d ? format(d, 'MMM d, yyyy') : null;
}

/**
 * True when a card is saved but not charged at checkout.
 * When paymentOption is provided, defer only when that option is recurring/payg
 * (a program supporting both may still let the parent pay in full today).
 */
export function isDeferredFirstCharge(
  program: ProgramLike,
  paymentOption?: PaymentOptionLike,
): boolean {
  if (paymentOption) return paymentOption === 'recurring' || paymentOption === 'payg';
  return !!(program.monthly_subscription || program.pay_as_you_go);
}

export type BillingSummary = {
  /** Amount actually charged at checkout for this line (per child count = 1). */
  dueToday: number;
  /** Short label for the "due today" side. */
  dueTodayLabel: string;
  /** Primary sentence, e.g. "Charged $260 today". */
  headline: string;
  /** Optional second sentence explaining the recurring/scheduled side. */
  detail?: string;
  /** True when nothing is charged at checkout. */
  deferred: boolean;
};

/**
 * Central source of truth for billing copy across the app. Given a program
 * and (optionally) the parent's selected payment option, returns a
 * standardized human-readable summary. Backend charge logic lives in
 * `process-registration-payment`; this helper mirrors it for display only.
 */
export function getBillingSummary(
  program: ProgramLike,
  paymentOption: PaymentOptionLike = null,
): BillingSummary {
  const monthly = program.price ?? 0;
  const sessionPrice = program.full_session_price ?? program.price ?? 0;
  const deposit = program.deposit_amount ?? 0;
  const firstDate = formatFirstBillDate(program);
  const dayOfMonth = program.payment_due_day_of_month ?? null;
  const monthlyClause = dayOfMonth
    ? `then monthly on the ${dayOfMonth}${ordinalSuffix(dayOfMonth)}`
    : 'then monthly thereafter';

  // Infer option when not provided
  const inferred: PaymentOptionLike =
    paymentOption ??
    (program.pay_as_you_go
      ? 'payg'
      : program.monthly_subscription
        ? 'recurring'
        : sessionPrice > 0
          ? 'full'
          : null);

  switch (inferred) {
    case 'payg':
      return {
        dueToday: 0,
        dueTodayLabel: '$0.00',
        headline: "You won't be charged today — card saved on file.",
        detail: firstDate
          ? `Billed monthly based on attendance, starting ${firstDate}.`
          : 'Billed monthly based on attendance.',
        deferred: true,
      };

    case 'recurring':
      return {
        dueToday: 0,
        dueTodayLabel: '$0.00',
        headline: "You won't be charged today — card saved on file.",
        detail: firstDate
          ? `First charge of $${monthly.toFixed(2)} on ${firstDate}, ${monthlyClause}.`
          : `First charge of $${monthly.toFixed(2)} on the program's first billing date, ${monthlyClause}.`,
        deferred: true,
      };

    case 'deposit': {
      const balance = Math.max(0, sessionPrice - deposit);
      const balanceRaw = program.final_payment_due_date || program.start_date;
      let balanceDate: string | null = null;
      if (balanceRaw) {
        try {
          balanceDate = format(parseDateOnly(balanceRaw), 'MMM d, yyyy');
        } catch {}
      }
      return {
        dueToday: deposit,
        dueTodayLabel: `$${deposit.toFixed(2)}`,
        headline: `$${deposit.toFixed(2)} due today (deposit).`,
        detail:
          balance > 0
            ? `Remaining $${balance.toFixed(2)} balance ${balanceDate ? `charged ${balanceDate}` : 'charged before the program starts'}.`
            : undefined,
        deferred: false,
      };
    }


    case 'payment_plan': {
      const n = program.payment_plan_installments ?? 0;
      const per = n > 0 ? sessionPrice / n : sessionPrice;
      const freq = program.payment_plan_frequency || 'monthly';
      return {
        dueToday: per,
        dueTodayLabel: `$${per.toFixed(2)}`,
        headline: `$${per.toFixed(2)} due today (installment 1 of ${n || '—'}).`,
        detail:
          n > 1
            ? `${n - 1} more ${freq} installment${n - 1 === 1 ? '' : 's'} of $${per.toFixed(2)}${
                dayOfMonth ? ` on the ${dayOfMonth}${ordinalSuffix(dayOfMonth)}` : ''
              }.`
            : undefined,
        deferred: false,
      };
    }

    case 'full':
    default:
      return {
        dueToday: sessionPrice,
        dueTodayLabel: `$${sessionPrice.toFixed(2)}`,
        headline: `$${sessionPrice.toFixed(2)} due today (paid in full).`,
        detail: undefined,
        deferred: false,
      };
  }
}
